import type {
  AgentPhase,
  AgentSideEffect,
  PresetActionId,
  PresetOption,
} from "@/types/createAgent";

export type TurnReadinessStatus = "ready" | "not_ready" | "unknown";

export interface BlockedAction {
  actionId: PresetActionId;
  reason: string;
}

export interface AllowedAction {
  actionId: PresetActionId;
  label: string;
  preset: PresetOption;
}

export interface TurnReadiness {
  status: TurnReadinessStatus;
  reason?: string;
  readyToGenerate?: boolean;
  blockedActions: BlockedAction[];
}

export interface AgentTurnPlan {
  assistantMessage: string;
  phase: AgentPhase;
  presets: PresetOption[];
  allowedActions: AllowedAction[];
  suggestionChips: PresetOption[];
  blockedActions: BlockedAction[];
  sideEffects: AgentSideEffect[];
  readiness: TurnReadiness;
}

export interface TurnReadinessInput {
  status?: TurnReadinessStatus;
  reason?: string;
  readyToGenerate?: boolean;
  ready?: boolean;
  blockedActions?: Array<Partial<BlockedAction> | null | undefined> | null;
  blockedActionIds?: PresetActionId[] | null;
}

export interface BuildAgentTurnPlanInput {
  message?: string | null;
  presets?: PresetOption[] | null;
  sideEffects?: AgentSideEffect[] | null;
  readiness?: TurnReadinessInput | null;
  phase: AgentPhase;
}

const ACTION_CHIP_TYPES = new Set<PresetOption["action"]>(["action", "modify"]);

function normalizeBlockedAction(
  action: Partial<BlockedAction> | null | undefined
): BlockedAction | null {
  if (!action?.actionId) return null;

  return {
    actionId: action.actionId,
    reason: action.reason?.trim() || "Action is not currently available.",
  };
}

export function splitPresetsByChipType(presets: PresetOption[]): {
  actionChips: PresetOption[];
  suggestionChips: PresetOption[];
} {
  return presets.reduce(
    (groups, preset) => {
      if (ACTION_CHIP_TYPES.has(preset.action)) {
        groups.actionChips.push(preset);
      } else {
        groups.suggestionChips.push(preset);
      }
      return groups;
    },
    { actionChips: [] as PresetOption[], suggestionChips: [] as PresetOption[] }
  );
}

export function normalizeTurnReadiness(
  readiness: TurnReadinessInput | null | undefined
): TurnReadiness {
  const explicitBlockedActions =
    readiness?.blockedActions
      ?.map(normalizeBlockedAction)
      .filter((action): action is BlockedAction => action !== null) ?? [];

  const blockedActionIds =
    readiness?.blockedActionIds?.map(actionId => ({
      actionId,
      reason: readiness.reason?.trim() || "Action is not currently available.",
    })) ?? [];

  const readyToGenerate = readiness?.readyToGenerate ?? readiness?.ready;
  const status =
    readiness?.status ??
    (readyToGenerate === true
      ? "ready"
      : readyToGenerate === false
        ? "not_ready"
        : "unknown");

  const inferredBlockedActions =
    readyToGenerate === false
      ? [
          {
            actionId: "generate_dress_front" as const,
            reason:
              readiness?.reason?.trim() ||
              "Needs more design detail before generation.",
          },
        ]
      : [];

  return {
    status,
    ...(readiness?.reason ? { reason: readiness.reason } : {}),
    ...(readyToGenerate !== undefined ? { readyToGenerate } : {}),
    blockedActions: dedupeBlockedActions([
      ...explicitBlockedActions,
      ...blockedActionIds,
      ...inferredBlockedActions,
    ]),
  };
}

export function blockActionsByReadiness(
  actionChips: PresetOption[],
  readiness: TurnReadiness
): {
  allowedActions: AllowedAction[];
  blockedActions: BlockedAction[];
} {
  const blockedById = new Map(
    readiness.blockedActions.map(blocked => [blocked.actionId, blocked])
  );

  const allowedActions = actionChips
    .filter(preset => !blockedById.has(preset.actionId))
    .map(preset => ({
      actionId: preset.actionId,
      label: preset.label,
      preset,
    }));

  return {
    allowedActions,
    blockedActions: readiness.blockedActions,
  };
}

export function buildAgentTurnPlan(
  input: BuildAgentTurnPlanInput
): AgentTurnPlan {
  const presets = input.presets ?? [];
  const sideEffects = input.sideEffects ?? [];
  const readiness = normalizeTurnReadiness(input.readiness);
  const { actionChips, suggestionChips } = splitPresetsByChipType(presets);
  const { allowedActions, blockedActions } = blockActionsByReadiness(
    actionChips,
    readiness
  );

  return {
    assistantMessage: input.message ?? "",
    phase: input.phase,
    presets,
    allowedActions,
    suggestionChips,
    blockedActions,
    sideEffects,
    readiness,
  };
}

function dedupeBlockedActions(
  blockedActions: BlockedAction[]
): BlockedAction[] {
  const seen = new Set<PresetActionId>();
  return blockedActions.filter(action => {
    if (seen.has(action.actionId)) return false;
    seen.add(action.actionId);
    return true;
  });
}
